import type { Episode } from './clinical/episode'
import type { StoredEpisode } from './storage'

const statusLabel = (status: Episode['status']) => status === 'resolution_candidate'
  ? 'RISOLUZIONE?'
  : status === 'resolved'
    ? 'RISOLTO'
    : status === 'closed'
      ? 'CHIUSO'
      : 'ATTIVO'

const diagnosisLabel = (episode: Episode) => {
  if (episode.diagnosis === 'DKA') return 'DKA'
  if (episode.diagnosis === 'EUGLYCEMIC_DKA') return 'DKA euglicemica'
  if (episode.diagnosis === 'HHS') return 'HHS'
  if (episode.diagnosis === 'MIXED_DKA_HHS') return 'Forma mista DKA/HHS'
  const provisional = episode.observations[0]?.result.provisionalPattern?.label
  return provisional || 'Non classificato'
}

const caseCode = (episode: Episode) => {
  const date = new Date(episode.startedAt)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const suffix = episode.id.replaceAll('-', '').slice(-5).toUpperCase()
  return `CASO-${y}${m}${d}-${suffix}`
}

const dateTime = (iso: string) => new Intl.DateTimeFormat('it-IT', {
  day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
}).format(new Date(iso))

export default function CaseArchivePanel({
  episodes,
  currentEpisode,
  open,
  onToggle,
  onNewCase,
  onOpenEpisode
}: {
  episodes: StoredEpisode[]
  currentEpisode: Episode | null
  open: boolean
  onToggle: () => void
  onNewCase: () => void
  onOpenEpisode: (episode: StoredEpisode) => void
}) {
  const activeCount = episodes.filter((episode) => episode.status !== 'closed').length
  const closedCount = episodes.filter((episode) => episode.status === 'closed').length

  return (
    <>
      <section className="case-nav panel">
        <div className="case-nav-copy">
          <p className="step">Gestione casi</p>
          {currentEpisode ? (
            <div className="case-current-line">
              <strong>{caseCode(currentEpisode)}</strong>
              <span className={`case-status ${currentEpisode.status}`}>{statusLabel(currentEpisode.status)}</span>
              <span className="case-current-diagnosis">{diagnosisLabel(currentEpisode)}</span>
            </div>
          ) : (
            <div className="case-current-line">
              <strong>Nuovo caso</strong>
              <span className="case-status draft">IN COMPILAZIONE</span>
              <span className="case-current-diagnosis">Nessun episodio ancora avviato</span>
            </div>
          )}
        </div>
        <div className="case-nav-actions">
          <button className="secondary case-button" type="button" onClick={onToggle}>
            {open ? 'Chiudi archivio' : `Casi (${episodes.length})`}
          </button>
          <button className="primary case-button" type="button" onClick={onNewCase}>+ Nuovo caso</button>
        </div>
      </section>

      {open && (
        <section className="case-archive panel" aria-label="Archivio casi">
          <div className="case-archive-heading">
            <div>
              <p className="step">Archivio locale</p>
              <h2>Casi registrati sul dispositivo</h2>
              <p className="muted">{activeCount} attivi/non chiusi · {closedCount} chiusi. Nessun dato identificativo è richiesto.</p>
            </div>
          </div>

          {!episodes.length ? (
            <div className="case-empty">Nessun caso registrato. Premi “Nuovo caso” per iniziare.</div>
          ) : (
            <div className="case-list">
              {episodes.map((episode) => {
                const selected = currentEpisode?.id === episode.id
                const updatedAt = episode.updatedAt || episode.startedAt
                return (
                  <article className={`case-row ${selected ? 'selected' : ''}`} key={episode.id}>
                    <div className="case-row-main">
                      <div className="case-row-title">
                        <strong>{caseCode(episode)}</strong>
                        <span className={`case-status ${episode.status}`}>{statusLabel(episode.status)}</span>
                      </div>
                      <div className="case-row-diagnosis">{diagnosisLabel(episode)}</div>
                      <div className="case-row-meta">
                        <span>Avvio {dateTime(episode.startedAt)}</span>
                        <span>Agg. {dateTime(updatedAt)}</span>
                        <span>{episode.observations.length} rilevazioni</span>
                      </div>
                    </div>
                    <button className="secondary case-open-button" type="button" onClick={() => onOpenEpisode(episode)} disabled={selected}>
                      {selected ? 'Aperto' : 'Apri'}
                    </button>
                  </article>
                )
              })}
            </div>
          )}
        </section>
      )}
    </>
  )
}
